/*
 * The formula shelf's card carousel.
 *
 * It upgrades a track that already works. `.ap-shelf__track` is an overflow-x
 * scroller with snap points, so before this file runs -- and if it never runs
 * -- the shelf is swipeable and every card is reachable. Everything here is the
 * layer on top: arrows, dots, rotation, and the loop.
 *
 * THE LOOP IS WHY THE SLIDES ARE CLONED. A plain track has two ends, so the
 * step across the boundary has to move more than one card: going from
 * "Mobility + Clear Sight" back to the start replaces both cards at once, which
 * reads as a skip rather than a step. Cloning the set either side of the real
 * one means the card after Clear Sight is simply ActivExtend, so every press
 * moves exactly one card in either direction, from anywhere, forever.
 *
 * The clones are built here and not in the Liquid, deliberately: source stays
 * five cards, so a visitor with no JavaScript gets five products and not
 * fifteen.
 *
 * THERE IS NO PAUSE CONTROL. Client direction, 2026-09-17, and a known trade
 * against WCAG 2.2.2 (Pause, Stop, Hide): at the shipped five-second interval
 * this shelf moves on its own with nothing a visitor can press to stop it. The
 * holds below are mitigation, not compliance. `rotate_seconds: 0` is the
 * accessible setting and it is a theme-editor field.
 *
 * ROTATION STOPS FOR EVERYTHING, and that is the point rather than a nicety:
 *
 *   - `prefers-reduced-motion: reduce`  never starts
 *   - the shelf is off screen           IntersectionObserver
 *   - the tab is in the background      visibilitychange
 *   - pointer over the shelf            pointerenter / pointerleave
 *   - focus inside the shelf            focusin / focusout
 *   - the visitor used the shelf        permanent, see stop()
 *
 * The last one is the brief. The client asked for rotation when a visitor
 * "stays static on that section"; moving the shelf under someone who has
 * started browsing it is the opposite of that, so any arrow, dot or manual
 * scroll ends rotation for the rest of the page's life.
 */

const REDUCED = '(prefers-reduced-motion: reduce)';
const SETTLE_MS = 140;

class ApFormulaCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ap-shelf-track]');
    this.controls = this.querySelector('[data-ap-shelf-controls]');
    if (!this.track || !this.controls) return;

    this.originals = Array.from(this.track.children);
    this.setLen = this.originals.length;
    if (this.setLen < 2) return;

    this.prev = this.querySelector('[data-ap-shelf-prev]');
    this.next = this.querySelector('[data-ap-shelf-next]');
    this.dotsBox = this.querySelector('[data-ap-shelf-dots]');

    this.interval = Number(this.dataset.rotateSeconds || 0) * 1000;
    this.motionOk = !window.matchMedia(REDUCED).matches;
    this.timer = null;
    this.stopped = false;
    this.blockers = new Set();

    this.buildLoop();
    this.buildDots();

    this.controls.hidden = false;
    // The arrows live outside the controls row, flanking the track, so each is
    // revealed on its own.
    if (this.prev) this.prev.hidden = false;
    if (this.next) this.next.hidden = false;

    this.prev?.addEventListener('click', () => this.nudge(-1, true));
    this.next?.addEventListener('click', () => this.nudge(1, true));

    /*
     * One debounced handler for every scroll, ours and the visitor's alike.
     * Normalising has to happen after the movement stops: doing it mid-scroll
     * fights the animation, and doing it never lets the shelf walk off the end
     * of the clones.
     */
    this.track.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(this.settleTimer);
        this.settleTimer = window.setTimeout(() => this.onSettled(), SETTLE_MS);
        if (!this.selfScrolling) this.stop();
      },
      { passive: true }
    );

    if (this.interval > 0 && this.motionOk) this.wireRotation();
  }

  disconnectedCallback() {
    this.halt();
    this.observer?.disconnect();
    window.clearTimeout(this.settleTimer);
  }

  /* ---------------------------------------------------------------- the loop */

  /*
   * One copy of the set either side of the real one, and the view parked on the
   * middle copy. A full set each way rather than just the two cards on screen:
   * it costs nothing but DOM (the images are the same URLs, so the browser
   * fetches each once) and it means the shelf can be walked five times in one
   * direction before it needs normalising, instead of once.
   */
  buildLoop() {
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    for (const node of this.originals) {
      before.append(this.cloneSlide(node));
      after.append(this.cloneSlide(node));
    }

    this.track.prepend(before);
    this.track.append(after);

    this.slides = Array.from(this.track.children);
    this.base = this.setLen;

    this.jumpTo(this.base);
  }

  /*
   * A clone is hidden from assistive technology and taken out of the tab order,
   * so a screen reader hears five formulas rather than fifteen and tabbing does
   * not walk the same card three times.
   *
   * It is NOT made inert. The forms inside are real and correct -- same variant,
   * same action -- so a pointer landing on a clone mid-scroll adds the right
   * thing to the cart. Inert would make that click do nothing instead, which is
   * a worse answer to a rarer problem.
   */
  cloneSlide(node) {
    const copy = node.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    copy.dataset.apShelfClone = '';
    for (const el of copy.querySelectorAll('a, button, input, select, textarea')) {
      el.setAttribute('tabindex', '-1');
    }
    return copy;
  }

  /* Which of the five formulas a DOM index is, whichever copy it belongs to. */
  logical(i) {
    return (((i - this.base) % this.setLen) + this.setLen) % this.setLen;
  }

  offsetOf(i) {
    return this.slides[i].offsetLeft - this.track.offsetLeft;
  }

  /*
   * Put the view back on the middle copy without moving anything on screen.
   *
   * The jump is a whole set wide, so the card under the reader's eye is the
   * same card in the same place; only its DOM index changes. That is the whole
   * trick, and it is why the boundary is invisible.
   */
  onSettled() {
    this.syncDots();

    let i = this.domCurrent;
    if (i < this.base) i += this.setLen;
    else if (i >= this.base + this.setLen) i -= this.setLen;
    else return;

    this.jumpTo(i);
    this.syncDots();
  }

  jumpTo(i) {
    this.selfScrolling = true;
    this.track.scrollTo({ left: this.offsetOf(i), behavior: 'auto' });
    window.clearTimeout(this.selfScrollTimer);
    this.selfScrollTimer = window.setTimeout(() => {
      this.selfScrolling = false;
    }, SETTLE_MS * 2);
  }

  /* --------------------------------------------------------------- rotation */

  wireRotation() {
    for (const [event, blocking] of [
      ['pointerenter', true],
      ['pointerleave', false],
      ['focusin', true],
      ['focusout', false],
    ]) {
      this.addEventListener(event, () => this.block('pointer-or-focus', blocking));
    }

    document.addEventListener('visibilitychange', () =>
      this.block('tab-hidden', document.hidden)
    );

    /*
     * Off screen is the common case on this page -- the shelf is the second
     * band and most visits never reach it -- so rotating while it is out of
     * view would burn a timer on a shelf nobody can see, and would land a
     * visitor scrolling down onto a card that is mid-move.
     */
    this.observer = new IntersectionObserver(
      ([entry]) => this.block('off-screen', !entry.isIntersecting),
      { threshold: 0.35 }
    );
    this.observer.observe(this);

    this.block('off-screen', true);
  }

  /* A named blocker, so four independent reasons to hold can overlap without
     any of them cancelling another's hold when it clears. */
  block(reason, on) {
    if (this.stopped) return;
    if (on) this.blockers.add(reason);
    else this.blockers.delete(reason);
    if (this.blockers.size) this.halt();
    else this.run();
  }

  run() {
    if (this.stopped || this.timer || this.interval <= 0) return;
    this.timer = window.setInterval(() => this.nudge(1, false), this.interval);
  }

  halt() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  /* Deliberate and permanent: the visitor has taken over, so the shelf stops
     moving on its own for the rest of the page's life. With no pause control
     this is the only way rotation ends, which is why it is not reversible. */
  stop() {
    if (this.stopped) return;
    this.stopped = true;
    this.halt();
  }

  /* ------------------------------------------------------------ positioning */

  /* The DOM index sitting at the left edge. There is no end to clamp against
     any more -- the clones mean every index has a neighbour on both sides. */
  get domCurrent() {
    const { scrollLeft } = this.track;
    let best = 0;
    let bestGap = Infinity;
    for (let i = 0; i < this.slides.length; i += 1) {
      const gap = Math.abs(this.offsetOf(i) - scrollLeft);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    }
    return best;
  }

  /* Whether a DOM index is fully on screen. Two cards show at a time, so two
     are true at once, and that is what the dots report. */
  isVisible(i) {
    const left = this.offsetOf(i) - this.track.scrollLeft;
    const width = this.slides[i].getBoundingClientRect().width;
    return left >= -2 && left + width <= this.track.clientWidth + 2;
  }

  /*
   * `byVisitor` separates the two callers. An arrow or a dot is the visitor
   * taking over and ends rotation; the timer's own advance must not, or the
   * first tick would stop the thing that fired it.
   *
   * Always exactly one card, in either direction, with no special case at the
   * boundary -- that is what the clones bought.
   */
  nudge(step, byVisitor) {
    if (byVisitor) this.stop();
    this.glideTo(this.domCurrent + step);
  }

  glideTo(i) {
    const slide = this.slides[i];
    if (!slide) return;

    this.selfScrolling = true;
    window.clearTimeout(this.selfScrollTimer);
    this.selfScrollTimer = window.setTimeout(() => {
      this.selfScrolling = false;
    }, 700);

    this.track.scrollTo({
      left: this.offsetOf(i),
      behavior: this.motionOk ? 'smooth' : 'auto',
    });
  }

  /* ------------------------------------------------------------ the dots */

  /*
   * One dot per formula, lit for every formula currently on screen -- so two
   * are lit at a time on a two-up shelf.
   *
   * Not one dot per scroll position: there are five formulas, the shelf loops,
   * and with no part-visible third card the dots are the only thing left that
   * says how many there are.
   */
  buildDots() {
    if (!this.dotsBox) return;
    this.dots = this.originals.map((slide, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ap-shelf__dot';
      // The card's own name, so the control says where it goes rather than
      // "slide 3 of 5", which tells a screen-reader user nothing about it.
      const name = slide.querySelector('.ap-formula-card__name')?.textContent?.trim();
      dot.setAttribute('aria-label', name || `Formula ${i + 1}`);
      dot.addEventListener('click', () => {
        this.stop();
        this.glideTo(this.base + i);
      });
      this.dotsBox.append(dot);
      return dot;
    });
    this.syncDots();
  }

  syncDots() {
    if (!this.dots) return;

    const lit = new Set();
    for (let i = 0; i < this.slides.length; i += 1) {
      if (this.isVisible(i)) lit.add(this.logical(i));
    }

    this.dots.forEach((dot, i) => {
      const on = lit.has(i);
      dot.classList.toggle('is-current', on);
      dot.setAttribute('aria-current', on ? 'true' : 'false');
    });

    // Nothing is disabled: the shelf loops, so there is no end to be spent at.
  }
}

if (!customElements.get('ap-formula-carousel')) {
  customElements.define('ap-formula-carousel', ApFormulaCarousel);
}
